import { useEffect, useState } from 'react';
import { supabase } from '../supabase.js';
import { baseSubject, readableText } from './emailText.js';
import { emailStatus, fetchEmail, formatWhen } from './staffAdmin.js';

/** The words of an HTML email when it has no plain-text version: quoted earlier emails, Gmail signatures and styles left out. */
function htmlToText(html) {
  // A document with no window: nothing in it runs or loads.
  const doc = document.implementation.createHTMLDocument('');
  doc.body.innerHTML = String(html);
  doc.querySelectorAll('style, script, head, blockquote, .gmail_quote, .gmail_signature, img').forEach((node) => node.remove());
  doc.querySelectorAll('br').forEach((node) => node.replaceWith('\n'));
  doc.querySelectorAll('p, div, li, tr, h1, h2, h3, h4, h5, h6').forEach((node) => node.append('\n'));
  return doc.body?.textContent ?? '';
}

/** The email as it was sent: the HTML in a locked-down frame (no scripts, forms or links), or the plain text. */
function Original({ email }) {
  if (email.html) return <iframe className="admin-email-body" title={`Email: ${email.subject}`} sandbox="" referrerPolicy="no-referrer" srcDoc={email.html}/>;
  if (email.text) return <pre className="admin-email-text">{email.text}</pre>;
  return <p className="mod-note">This email has no content to show.</p>;
}

/**
 * The whole of one email, read from Resend when it is opened. `compact` (inside a conversation) puts who, to whom, when and,
 * for Naima's emails, delivery on one line, then only what this email says: no quoted earlier emails or signature, with the
 * original a click away. Its subject shows only when it isn't the conversation's `threadSubject` (bar "Re:").
 */
export default function EmailDetail({ id, kind, compact = false, threadSubject = '' }) {
  const [state, setState] = useState({ email: null, error: '' });
  const [original, setOriginal] = useState(false);
  useEffect(() => {
    let live = true;
    setState({ email: null, error: '' });
    fetchEmail(supabase, { id, kind }).then(
      (email) => { if (live) setState({ email, error: '' }); },
      (cause) => { if (live) setState({ email: null, error: cause.message }); },
    );
    return () => { live = false; };
  }, [id, kind]);

  const { email, error } = state;
  if (error) return <p className="mod-error" role="alert">{error}</p>;
  if (!email) return <p className="mod-note">Loading this email…</p>;
  const status = emailStatus(email.status);
  const cc = email.cc || [];
  const replyTo = email.replyTo || [];

  if (compact) {
    const words = readableText(email.text || (email.html ? htmlToText(email.html) : ''));
    return (
      <div className="admin-mail">
        <p className="admin-mail-meta">
          <span className="admin-mail-from">{email.from}</span>
          {` → ${(email.to || []).join(', ')}`}
          {cc.length > 0 && ` · cc ${cc.join(', ')}`}
          {replyTo.length > 0 && ` · reply to ${replyTo.join(', ')}`}
          {` · ${formatWhen(email.at)}`}
          {kind === 'sent' && <>{' · '}<span className={`admin-status is-${status.tone}`}>{status.label}</span></>}
        </p>
        {baseSubject(email.subject) !== baseSubject(threadSubject) && <p className="admin-mail-subject">{email.subject || 'No subject'}</p>}
        {words ? <p className="admin-mail-text">{words}</p> : <p className="mod-note">This email has no text.</p>}
        <button type="button" className="admin-link admin-mail-original" aria-expanded={original} onClick={() => setOriginal((open) => !open)}>
          {original ? 'Hide the original' : 'Show the original'}
        </button>
        {original && <Original email={email}/>}
      </div>
    );
  }

  return (
    <div className="admin-email-detail">
      <dl className="mod-fields">
        <dt>From</dt><dd>{email.from}</dd>
        <dt>To</dt><dd>{(email.to || []).join(', ')}</dd>
        {cc.length > 0 && <><dt>Cc</dt><dd>{cc.join(', ')}</dd></>}
        {replyTo.length > 0 && <><dt>Reply-to</dt><dd>{replyTo.join(', ')}</dd></>}
        <dt>When</dt><dd>{formatWhen(email.at)}</dd>
        {kind === 'sent' && <><dt>Status</dt><dd><span className={`admin-status is-${status.tone}`}>{status.label}</span></dd></>}
      </dl>
      <Original email={email}/>
    </div>
  );
}
